import { createContext, useContext } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
} from 'amazon-cognito-identity-js';

import { message, Modal } from 'antd';
import { COGNITO_CLIENT_ID, COGNITO_POOL_ID } from '../settings/constants';
import ChangePasswordModal from '../container/LoginPage/ChangePassModal';

const Context = createContext();

const Pool = new CognitoUserPool({
    UserPoolId: COGNITO_POOL_ID,
    ClientId: COGNITO_CLIENT_ID,
});

const Provider = ({ children }) => {
    const [user, setUser] = useState(undefined);

    const getAttributes = (user) =>
        new Promise((resolve, reject) => {
            user.getUserAttributes((err, attributes) => {
                if (err) return reject(err);

                const results = attributes.reduce(
                    (prev, { Name, Value }) => ({ ...prev, [Name]: Value }),
                    {}
                );

                resolve(results);
            });
        });

    const getSession = async () => {
        const user = Pool.getCurrentUser();

        if (!user) {
            return setUser(null);
        }
        user.getSession(async (err, session) => {
            if (err) return setUser(null);

            const attributes = await getAttributes(user);
            setUser({
                user,
                session,
                ...attributes,
            });
        });
    };

    const refreshToken = async () => {
        if (!user) {
            return;
        }

        return new Promise((resolve, reject) => {
            const refreshToken = user.session.getRefreshToken();

            user.user.refreshSession(refreshToken, async (err, session) => {
                if (err) {
                    setUser(null);
                    return reject(null);
                }

                user.session = session;
                resolve(user);
            });
        });
    };

    const authenticate = async (Username, Password) =>
        await new Promise((resolve, reject) => {
            const user = new CognitoUser({ Username, Pool });
            const authDetails = new AuthenticationDetails({
                Username,
                Password,
            });

            user.authenticateUser(authDetails, {
                onSuccess: (data) => {
                    getSession();
                    resolve(data);
                },

                onFailure: (err) => {
                    reject(err);
                },

                newPasswordRequired: (data, req) => {
                    const modal = Modal.warning({
                        title: 'Password change required',
                        icon: null,
                        width: 600,
                        okType: 'default',
                        okText: 'cancel',
                        content: (
                            <ChangePasswordModal
                                showName
                                afterClose={() => getSession()}
                                updateInfo={({ password, name }) =>
                                    new Promise((resolve, reject) =>
                                        user.completeNewPasswordChallenge(
                                            password,
                                            //{ name, given_name: '-' },
                                            { name },
                                            {
                                                onSuccess: () => {
                                                    modal.destroy();
                                                    authenticate(
                                                        Username,
                                                        password
                                                    );
                                                },
                                                onFailure: reject,
                                            }
                                        )
                                    )
                                }
                            />
                        ),
                    });
                    resolve(data);
                },
            });
        });

    const sendCode = (email) => {
        const user = new CognitoUser({ Username: email, Pool });

        user.forgotPassword({
            onSuccess: ({ CodeDeliveryDetails: { Destination } }) => {
                message.success(`Code send to: ${Destination}`);
            },
            onFailure: (err) => {
                message.error(err.message);
            },
        });
    };

    const forgotPassword = (data) => {
        const modal = Modal.warning({
            title: 'Forgot Password',
            icon: null,
            width: 600,
            okType: 'default',
            okText: 'cancel',
            content: (
                <ChangePasswordModal
                    showCode={{
                        sendCode,
                    }}
                    afterClose={() => getSession()}
                    updateInfo={({ password, mail, code }) =>
                        new Promise((resolve, reject) => {
                            const user = new CognitoUser({
                                Username: mail,
                                Pool,
                            });
                            user.confirmPassword(code, password, {
                                onSuccess: (data) => {
                                    message.success(`Password changed!`);
                                    modal.destroy();
                                    authenticate(mail, password);
                                },
                                onFailure: reject,
                            });
                        })
                    }
                />
            ),
        });
    };

    const logOut = () => {
        const user = Pool.getCurrentUser();

        if (user) {
            user.signOut();
            getSession();
        }
    };

    const isTokenExpired = () =>
        user?.session?.idToken?.payload.exp - 60 < Date.now() / 1000;

    useEffect(() => {
        getSession();
    }, []);

    const getAuthToken = () => user?.session?.idToken?.jwtToken;

    return (
        <Context.Provider
            value={{
                login: authenticate,
                user,
                refreshToken,
                forgotPassword,
                logOut,
                getAuthToken,
                isTokenExpired,
            }}
        >
            {user !== undefined && children}
        </Context.Provider>
    );
};

export const useAuth = () => {
    return useContext(Context);
};

export default Provider;
